/**
 * Copyright © 2026 Adnuntius AS.
 *
 * ModelComms fans out model/rel/comm updates to route siblings (title, context,
 * analysis). Synchronous notify during the publisher's CD turn NG0100s those
 * subscribers' bindings — always deliver on the next macrotask.
 */
import * as _ from "lodash";

export class ModelComms {
  private subs = [];
  private currentModel: any;
  private currentType: string;
  private currentRelData: any;
  private isNew = false;

  constructor(private readonly scheduleViewRefresh: () => void = () => undefined) {
  }

  addSubscriber(subFunc, blockImmediateCallback?) {
    this.subs.push(subFunc);

    if (!blockImmediateCallback && this.currentModel) {
      const modelSnap = _.cloneDeep(this.currentModel);
      const isNewSnap = this.isNew;
      this.scheduleNotify(() => {
        subFunc(modelSnap, null, isNewSnap);
      });
    }
    if (!blockImmediateCallback && this.currentRelData) {
      const relSnap = _.cloneDeep(this.currentRelData);
      const typeSnap = this.currentType;
      const isNewSnap = this.isNew;
      this.scheduleNotify(() => {
        subFunc(relSnap, typeSnap, isNewSnap);
      });
    }
  }

  relDataUpdated(relData, type: string) {
    this.currentRelData = relData;
    this.currentType = type || "undefinedType";
    const relSnap = _.cloneDeep(this.currentRelData);
    const typeSnap = type;
    const isNewSnap = this.isNew;
    const subscribers = this.subs.slice();
    this.scheduleNotify(() => {
      _.forEach(subscribers, (s) => {
        s(_.cloneDeep(relSnap), typeSnap, isNewSnap);
      });
    });
  }

  modelUpdated(model, isNew) {
    this.currentModel = model;
    // One-arg callers must not clobber a previously published boolean isNew.
    if (typeof isNew === 'boolean') {
      this.isNew = isNew;
    }
    const modelSnap = _.cloneDeep(this.currentModel);
    const isNewSnap = this.isNew;
    const subscribers = this.subs.slice();
    this.scheduleNotify(() => {
      _.forEach(subscribers, (s) => {
        s(_.cloneDeep(modelSnap), null, isNewSnap);
      });
    });
  }

  commMade(data) {
    const dataSnap = _.cloneDeep(data);
    const subscribers = this.subs.slice();
    this.scheduleNotify(() => {
      _.forEach(subscribers, (s) => {
        s(_.cloneDeep(dataSnap), "commMade");
      });
    });
  }

  /** Post-CD delivery so subscribers can update template bindings safely. */
  private scheduleNotify(deliver: () => void): void {
    setTimeout(() => {
      try {
        deliver();
      } finally {
        this.scheduleViewRefresh();
      }
    }, 0);
  }
}
